
import { defineStore } from 'pinia'
import Echo from 'laravel-echo'
import axios from 'axios'

import { stopTimer } from '@/composables/useTimeLogout'
import { authChannel } from '@/utils/authChannel'

const API = '/api/v1/auth'

function getErrorMessage(error, fallback) {
  if (
    error.code === 'ECONNABORTED' ||
    error.code === 'ETIMEDOUT'
  ) {
    return 'La solicitud tardó demasiado. Intenta nuevamente.'
  }

  const data = error.response?.data

  if (typeof data?.message === 'string') {
    return data.message
  }

  if (data?.errors) {
    if (Array.isArray(data.errors)) {
      return data.errors.join(' ')
    }

    if (typeof data.errors === 'object') {
      return Object.values(data.errors)
        .flat()
        .join(' ')
    }
  }

  return fallback
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    authUser: null,
    authToken: null,
    device: null,
    device_token:null,
    tokenExpirationDate: null,
    permissions: [],
    mustChangePasswordModal: false,
    expires_in: 15,
    loading: false,
    errors: null,
    forgotPasswordMessage: null,
    resetPasswordMessage: null,

    // Estado no persistente de Passkeys
    passkeyLoading: false,
  }),

  getters: {
    user: (state) => state.authUser,
    token: (state) => state.authToken,
    permisos: (state) => state.permissions,

    isAuthenticated: (state) =>
      Boolean(state.authUser && state.authToken),

    can: (state) => (permission) =>
      state.permissions.includes(permission),

    passkeySupported: () =>
      typeof window !== 'undefined' &&
      window.isSecureContext &&
      typeof window.PublicKeyCredential !== 'undefined',
  },

  actions: {
    clearMessages() {
      this.errors = null
      this.forgotPasswordMessage = null
      this.resetPasswordMessage = null
    },

    /*
    |--------------------------------------------------------------------------
    | COMPLETAR AUTENTICACIÓN
    |--------------------------------------------------------------------------
    | Compartido por contraseña y Passkey.
    | Laravel debe devolver una respuesta equivalente
    | para ambos métodos de autenticación.
    */
    completeLogin(data) {
      if (!data?.token || !data?.user) {
        throw new Error('Respuesta de autenticación incompleta.')
      }

      // Desconectar una conexión anterior
      this.disconnectEcho()

      this.authToken = data.token
      this.authUser = data.user
      this.device = data.device ?? null
      this.expires_in = Number(data.expires_in ?? 15)

      this.tokenExpirationDate = data.expires_at
        ? new Date(data.expires_at)
        : new Date(Date.now() + this.expires_in * 60 * 1000)

      this.permissions = Array.isArray(data.permissions)
        ? [...data.permissions]
        : []

      // Nunca conservar el valor de un usuario anterior
      this.mustChangePasswordModal =
        Boolean(data.mustChangePassword)

      this.errors = null

      this.initializeEcho()

      this.router.replace({
        name: data.homeView || 'dashboard',
      })

      return true
    },

    /*
    |--------------------------------------------------------------------------
    | LOGIN TRADICIONAL
    |--------------------------------------------------------------------------
    */
    async login(form) {
      if (this.loading || this.passkeyLoading) return false

      this.loading = true
      this.errors = null

      try {
        const deviceToken = localStorage.getItem('device_token')

        const headers = {}

        if (deviceToken) {
          headers['X-Device-Token'] = deviceToken
        }

        const { data } = await axios.post(
          `${API}/login`,
          {
            email: form.email,
            password: form.password,
          },
          {
            headers,
            timeout: 15000,
          }
        )

        return this.completeLogin(data)

      } catch (error) {
        this.errors = getErrorMessage(
          error,
          'No fue posible iniciar sesión.'
        )

        return false

      } finally {
        this.loading = false
      }
    },

    /*
    |--------------------------------------------------------------------------
    | LOGIN CON PASSKEY
    |--------------------------------------------------------------------------
    | Requiere endpoints WebAuthn en Laravel:
    |
    | POST /passkeys/authenticate/options
    | POST /passkeys/authenticate/verify
    |
    | El backend debe asociar cada desafío a una sesión
    | temporal anónima y verificarlo una sola vez.
    */

    async loginWithPasskey() {
      if (this.loading || this.passkeyLoading) return false

      this.errors = null

      if (!this.passkeySupported) {
        this.errors =
          'Este navegador o conexión no permite usar Passkeys.'
        return false
      }

      this.passkeyLoading = true

      try {
        const { startAuthentication } = await import(
          '@simplewebauthn/browser'
        )

        // 1. Solicitar desafío al backend
        const { data: challenge } = await axios.post(
          `${API}/passkeys/authenticate/options`,
          {},
          {
            timeout: 15000
          }
        )

        // 2. Solicitar autenticación biométrica o PIN
        const credential = await startAuthentication({
          optionsJSON: challenge.options
        })

        // 3. Verificar credencial en Laravel
        const deviceToken = localStorage.getItem('device_token')

        const headers = {}

        if (deviceToken) {
          headers['X-Device-Token'] = deviceToken
        }

        const { data } = await axios.post(
          `${API}/passkeys/authenticate/verify`,
          {
            challenge_id: challenge.challenge_id,
            credential
          },
          {
            headers,
            timeout: 15000
          }
        )

        // 4. Crear sesión habitual de Medical Dhara
        return this.completeLogin(data)

      } catch (error) {
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'AbortError'
        ) {
          this.errors =
            'La autenticación fue cancelada o no se completó.'
        } else {
          this.errors = getErrorMessage(
            error,
            'No fue posible iniciar sesión con Passkey.'
          )
        }

        return false

      } finally {
        this.passkeyLoading = false
      }
    },

    /*
    |--------------------------------------------------------------------------
    | REGISTRAR UNA NUEVA PASSKEY
    |--------------------------------------------------------------------------
    | Solo para usuarios autenticados.
    | El backend debe exigir reautenticación reciente
    | antes de permitir agregar credenciales.
    */

    async registerPasskey(name = 'Mi dispositivo') {
      if (!this.authToken || !this.authUser) {
        this.errors = 'Debes iniciar sesión.'
        return false
      }

      if (this.passkeyLoading) return false

      if (!this.passkeySupported) {
        this.errors = 'Este dispositivo no admite Passkeys.'
        return false
      }

      this.passkeyLoading = true
      this.errors = null

      try {
        const { startRegistration } = await import(
          '@simplewebauthn/browser'
        )

        const config = {
          headers: {
            Authorization: `Bearer ${this.authToken}`
          },
          timeout: 15000
        }

        // 1. Solicitar opciones de registro
        const { data: challenge } = await axios.post(
          `${API}/passkeys/register/options`,
          {},
          config
        )

        // 2. Registrar credencial en el dispositivo
        const credential = await startRegistration({
          optionsJSON: challenge.options
        })

        // 3. Verificar y almacenar Passkey en Laravel
        const { data } = await axios.post(
          `${API}/passkeys/register/verify`,
          {
            challenge_id: challenge.challenge_id,
            credential,
            name
          },
          config
        )

        return data

      } catch (error) {
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'AbortError'
        ) {
          this.errors =
            'El registro de Passkey fue cancelado.'
        } else {
          this.errors = getErrorMessage(
            error,
            'No fue posible registrar la Passkey.'
          )
        }

        return false

      } finally {
        this.passkeyLoading = false
      }
    },


    /*
    |--------------------------------------------------------------------------
    | LISTAR PASSKEYS DEL USUARIO
    |--------------------------------------------------------------------------
    */
    async getPasskeys() {
      if (!this.authToken) return []

      try {
        const { data } = await axios.get(
          `${API}/passkeys`,
          {
            headers: {
              Authorization: `Bearer ${this.authToken}`,
            },
          }
        )

        return Array.isArray(data)
          ? data
          : data.passkeys ?? []

      } catch (error) {
        this.errors = getErrorMessage(
          error,
          'No fue posible consultar las Passkeys.'
        )

        return []
      }
    },

    /*
    |--------------------------------------------------------------------------
    | REVOCAR PASSKEY
    |--------------------------------------------------------------------------
    | Laravel debe verificar propiedad de la credencial
    | y exigir reautenticación reciente.
    */
    async deletePasskey(id) {
      if (!this.authToken) return false

      try {
        await axios.delete(
          `${API}/passkeys/${encodeURIComponent(id)}`,
          {
            headers: {
              Authorization: `Bearer ${this.authToken}`,
            },
          }
        )

        return true

      } catch (error) {
        this.errors = getErrorMessage(
          error,
          'No fue posible eliminar la Passkey.'
        )

        return false
      }
    },

    /*
    |--------------------------------------------------------------------------
    | RECUPERAR CONTRASEÑA
    |--------------------------------------------------------------------------
    */
    async forgotPassword(form) {
      this.loading = true
      this.clearMessages()

      try {
        const { data } = await axios.post(
          `${API}/forgot-password`,
          {
            identifier: form.identifier,
          }
        )

        this.forgotPasswordMessage =
          data.message ||
          'Si existe una cuenta asociada, recibirás un correo.'

        return true

      } catch (error) {
        this.errors = getErrorMessage(
          error,
          'No fue posible procesar la solicitud.'
        )

        return false

      } finally {
        this.loading = false
      }
    },

    /*
    |--------------------------------------------------------------------------
    | RESTABLECER CONTRASEÑA
    |--------------------------------------------------------------------------
    */
    async resetpassword(form) {
      this.loading = true
      this.clearMessages()

      try {
        const { data } = await axios.post(
          `${API}/reset-password`,
          {
            token: form.token,
            email: form.email,
            password: form.password,
            password_confirmation: form.password_confirmation,
          }
        )

        this.resetPasswordMessage =
          data.message || 'Contraseña restablecida correctamente.'

        return true

      } catch (error) {
        this.errors = getErrorMessage(
          error,
          'No pudimos restablecer la contraseña.'
        )

        return false

      } finally {
        this.loading = false
      }
    },

    /*
    |--------------------------------------------------------------------------
    | LIMPIAR ESTADO DE AUTENTICACIÓN
    |--------------------------------------------------------------------------
    */
    clearAuth() {
      this.disconnectEcho()

      this.authToken = null
      this.authUser = null
      this.device = null
      this.tokenExpirationDate = null
      this.permissions = []
      this.mustChangePasswordModal = false
      this.expires_in = 15

      this.clearMessages()

      // Compatibilidad con el almacenamiento anterior
      localStorage.removeItem('token')

      stopTimer()
    },

    /*
    |--------------------------------------------------------------------------
    | LOGOUT
    |--------------------------------------------------------------------------
    */
    async logout() {
      const token = this.authToken

      try {
        if (token) {
          await axios.get(
            `${API}/logout`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              timeout: 15000,
            }
          )
        }
      } catch (error) {
        console.error('Error al cerrar sesión:', error)
      } finally {
        this.clearAuth()

        authChannel.postMessage({
          type: 'logout',
        })

        this.router.replace('/login')
      }
    },

    /*
    |--------------------------------------------------------------------------
    | VERIFICAR SESIÓN
    |--------------------------------------------------------------------------
    */
    async checkAuth() {
      if (!this.authToken) {
        this.clearAuth()
        return false
      }

      try {
        const { data } = await axios.get(
          `${API}/profile`,
          {
            headers: {
              Authorization: `Bearer ${this.authToken}`,
            },
            timeout: 15000,
          }
        )

        // No reemplazamos authUser porque desconocemos
        // la estructura exacta de la respuesta /profile.
        return data

      } catch (error) {
        if (error.response?.status === 401) {
          this.clearAuth()
        }

        return false
      }
    },

    /*
    |--------------------------------------------------------------------------
    | RENOVAR TOKEN
    |--------------------------------------------------------------------------
    | Conserva la ruta existente.
    | El backend debe validar el Bearer Token y obtener
    | la identidad desde la autenticación, no desde user_id.
    */
    async refreshToken() {
      if (!this.authToken) return false

      try {
        const { data } = await axios.post(
          `${API}/refreh-token`,
          {},
          {
            headers: {
              Authorization: `Bearer ${this.authToken}`,
            },
            timeout: 15000,
          }
        )

        if (!data.token) {
          throw new Error('No se recibió un token válido.')
        }

        this.authToken = data.token

        if (data.expires_in) {
          this.expires_in = Number(data.expires_in)
        }

        this.tokenExpirationDate = data.expires_at
          ? new Date(data.expires_at)
          : new Date(
            Date.now() + this.expires_in * 60 * 1000
          )

        // Actualizar credenciales de Echo
        this.initializeEcho()

        return true

      } catch (error) {
        if (error.response?.status === 401) {
          this.clearAuth()
        } else {
          console.error('Error renovando token:', error)
        }

        return false
      }
    },

    /*
    |--------------------------------------------------------------------------
    | RENOVACIÓN AUTOMÁTICA
    |--------------------------------------------------------------------------
    | Solo un intervalo activo por instancia.
    */
    startAutoRefresh(timeExpire = 1) {
      this.stopAutoRefresh()

      const minutes = Math.max(1, Number(timeExpire) || 1)

      this._refreshInterval = setInterval(async () => {
        if (!this.tokenExpirationDate || !this.authToken) {
          return
        }

        if (this._refreshInProgress) return

        const expiration = new Date(
          this.tokenExpirationDate
        ).getTime()

        const remaining = expiration - Date.now()
        const threshold = 5 * 60 * 1000

        if (remaining > 0 && remaining <= threshold) {
          this._refreshInProgress = true

          try {
            await this.refreshToken()
          } finally {
            this._refreshInProgress = false
          }
        }

        if (remaining <= 0) {
          this.stopAutoRefresh()
          this.clearAuth()

          authChannel.postMessage({
            type: 'logout',
          })

          this.router.replace('/login')
        }
      }, minutes * 60 * 1000)
    },

    stopAutoRefresh() {
      if (this._refreshInterval) {
        clearInterval(this._refreshInterval)
        this._refreshInterval = null
      }
    },

    /*
    |--------------------------------------------------------------------------
    | DESCONECTAR LARAVEL ECHO
    |--------------------------------------------------------------------------
    */
    disconnectEcho() {
      if (this.echo) {
        this.echo.disconnect()
        this.echo = null
      }

      if (typeof window !== 'undefined') {
        window.Echo = null
      }
    },

    /*
    |--------------------------------------------------------------------------
    | INICIALIZAR LARAVEL ECHO
    |--------------------------------------------------------------------------
    */
    initializeEcho() {
      if (!this.authToken) return

      this.disconnectEcho()

      try {
        this.echo = new Echo({
          broadcaster: 'pusher',
          key: import.meta.env.VITE_PUSHER_APP_KEY,
          cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER,

          authEndpoint:
            `${import.meta.env.VITE_API_URL}/api/broadcasting/auth`,

          encrypted: false,
          wsPort: 80,
          wssPort: 443,
          wsHost: import.meta.env.VITE_PUSHER_HOST,

          enabledTransports: ['ws', 'wss'],

          auth: {
            headers: {
              Authorization: `Bearer ${this.authToken}`,
              Accept: 'application/json',
            },
          },
        })

        window.Echo = this.echo

      } catch (error) {
        console.error(
          'Error inicializando Laravel Echo:',
          error
        )
      }
    },
  },

  /*
  |--------------------------------------------------------------------------
  | PERSISTENCIA PINIA
  |--------------------------------------------------------------------------
  | Conservamos temporalmente la estructura actual.
  | Para producción clínica conviene migrar el Bearer
  | Token a un mecanismo de almacenamiento más seguro.
  */
  persist: {
    pick: [
      'authUser',
      'authToken',
      'device',
      // 'device_token',
      'tokenExpirationDate',
      'permissions',
      'expires_in',
      'mustChangePasswordModal',
    ],
  },
})
